import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./UploadDocuments.module.css";
import { useUser } from "../../contexts/UserContext";
import apiService from "../../services/apiService";

import eyeIcon from "../assets/eye-icon.png";
import eyeIconStriked from "../assets/eye-striked-icon.png";

const business_type_details_map = [
  {
    business_type: "individual",
    info_fields: {
      business_name: "Business Name",
      name: "Name",
      aadhaar_number: "Aadhaar Number",
      pan_number: "PAN Number",
    },
  },
  {
    business_type: "proprietorship",
    info_fields: {
      business_name: "Business Name",
      aadhaar_number: "Aadhaar Number",
      pan_number: "PAN Number",
    },
  },
  {
    business_type: "partnership",
    info_fields: { business_name: "Business Name" },
  },
  {
    business_type: "limited_liability_partnership",
    info_fields: {
      business_name: "Business Name",
      company_pan_number: "Company PAN Number",
    },
  },
  {
    business_type: "one_person_company",
    info_fields: {
      business_name: "Business Name",
      company_pan_number: "Company PAN Number",
      gst_number: "GSTIN Number",
    },
  },
  {
    business_type: "private_limited_company",
    info_fields: {
      business_name: "Business Name",
      company_pan_number: "Company PAN Number",
      gst_number: "GSTIN Number",
    },
  },
];

const documents_required_map = {
  individual: [
    { name: "Aadhaar Card", key: "aadhaar_card" },
    { name: "PAN Card", key: "pan_card" },
    { name: "IT Returns", key: "it_returns" },
    { name: "Bank Statement (past 6 months)", key: "bank_statement" },
    { name: "Showroom pic", key: "showroom_pic" },
  ],
  proprietorship: [
    { name: "Aadhaar Card", key: "aadhaar_card" },
    { name: "PAN Card", key: "pan_card" },
    { name: "IT Returns", key: "it_returns" },
    { name: "Bank Statement (past 6 months)", key: "bank_statement" },
    { name: "Showroom pic", key: "showroom_pic" },
  ],
  limited_liability_partnership: [
    { name: "Company PAN Card", key: "company_pan_card" },
    {
      name: "Certificate of Incorporation",
      key: "certificate_of_incorporation",
    },
    { name: "Company / Business IT Returns", key: "company_it_returns" },
    { name: "Company Bank Statement (past 6 months)", key: "bank_statement" },
    { name: "Showroom pic", key: "showroom_pic" },
  ],
  one_person_company: [
    { name: "Company PAN Card", key: "company_pan_card" },
    {
      name: "Certificate of Incorporation",
      key: "certificate_of_incorporation",
    },
    { name: "Memorandum of Association", key: "memorandum_of_association" },
    { name: "Articles of Association", key: "articles_of_association" },
    { name: "Company / Business IT Returns", key: "company_it_returns" },
    { name: "Company Bank Statement (past 6 months)", key: "bank_statement" },
    { name: "Showroom pic", key: "showroom_pic" },
  ],
  partnership: [
    { name: "Company PAN Card", key: "company_pan_card" },
    {
      name: "Certificate of Incorporation",
      key: "certificate_of_incorporation",
    },
    { name: "Company / Business IT Returns", key: "company_it_returns" },
    { name: "Company Bank Statement (past 6 months)", key: "bank_statement" },
    { name: "Showroom pic", key: "showroom_pic" },
  ],

  signatory_proofs: [
    { name: "Aadhaar Card", key: "aadhaar_card" },
    { name: "PAN Card", key: "pan_card" },
    { name: "Profile Picture", key: "profile_pic" },
  ],
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

function inferTypeFromUrlOrName(urlOrName = "") {
  const lower = (urlOrName || "").split("?")[0].toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (/\.(jpe?g|jpg|jpeg|png|gif|webp)$/.test(lower)) return "image";
  return "unknown";
}

function isImageType(type) {
  if (!type) return false;
  return type.startsWith("image") || type === "image";
}
function isPdfType(type) {
  if (!type) return false;
  return type.includes("pdf") || type === "application/pdf";
}

export default function UploadDocuments() {
  const [signatories, setSignatories] = useState([]);
  const signatoryRefs = useRef([]);
  const [uploadedFiles, setUploadedFiles] = useState({}); // { key: { url, type, name } }
  const [uploadStatus, setUploadStatus] = useState({});
  const [signatoryProofs, setSignatoryProofs] = useState({});
  const [signatoryUploadStatus, setSignatoryUploadStatus] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [checkingNavigate, setCheckingNavigate] = useState(true);
  const [validating, setValidating] = useState(false);
  const [isNotValid, setIsNotValid] = useState(false);
  const [notValidComments, setNotValidComments] = useState("");
  const [showNotValidPopup, setShowNotValidPopup] = useState(false);
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();

  const [minimized, setMinimized] = useState(false);

  // render helper
  const renderPreview = (fileObj, docName, onClick) => {
    if (!fileObj) {
      return (
        <div className={styles.placeholder} onClick={onClick}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12V4m0 0L8 8m4-4l4 4"
            />
          </svg>
        </div>
      );
    }

    const { url, type, name } = fileObj;
    const displayName = name || docName;

    if (isImageType(type)) {
      return (
        <img
          src={url}
          alt={displayName}
          onClick={onClick}
          style={{ cursor: "pointer", maxWidth: "100%", maxHeight: "100%" }}
        />
      );
    }

    // PDF or other non-image: show document icon + name
    if (isPdfType(type) || type === "unknown" || !type) {
      return (
        <div
          className={styles.docFallback}
          onClick={onClick}
          style={{ cursor: "pointer", textAlign: "center" }}
        >
          {/* Document SVG icon */}
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14 3v6h6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect x="8" y="13" width="8" height="2" rx="1" />
          </svg>
          <div
            className={styles.docName}
            style={{ marginTop: 8, fontSize: 12, wordBreak: "break-word" }}
          >
            {displayName}
          </div>
        </div>
      );
    }

    // fallback to image attempt
    return (
      <img
        src={url}
        alt={displayName}
        onClick={onClick}
        style={{ cursor: "pointer", maxWidth: "100%", maxHeight: "100%" }}
      />
    );
  };

  // 🟧 upload handler
  const handleUpload = async (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadStatus((prev) => ({ ...prev, [key]: "uploading" }));

    try {
      const fileBase64 = await fileToBase64(file);
      const fileBase64Data = fileBase64.split(",")[1];
      const extension = file.name.split(".").pop();
      const fileName = key;
      const payload = {
        file: fileBase64Data,
        document_type: key,
        metadata: {
          contentType: file.type,
          originalName: file.name,
          size: file.size,
          extension: extension,
        },
      };

      const response = await apiService.dealerOnboardingUploadDocuments(
        payload
      );
      if (response?.data?.error === false) {
        const previewUrl =
          response.data?.document?.url ?? URL.createObjectURL(file);
        const contentTypeFromResponse =
          response.data?.document?.contentType ||
          response.data?.document?.metadata?.contentType;
        const inferredType =
          contentTypeFromResponse ||
          file.type ||
          inferTypeFromUrlOrName(previewUrl) ||
          inferTypeFromUrlOrName(file.name);

        setUploadedFiles((prev) => ({
          ...prev,
          [key]: { url: previewUrl, type: inferredType, name: file.name },
        }));
        setUploadStatus((prev) => ({ ...prev, [key]: "success" }));
      } else {
        throw new Error(response?.data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadStatus((prev) => ({ ...prev, [key]: "error" }));
    }
  };

  const handleSignatoryUpload = async (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    setSignatoryUploadStatus((prev) => ({ ...prev, [key]: "uploading" }));

    try {
      const fileBase64 = await fileToBase64(file);
      const fileBase64Data = fileBase64.split(",")[1];
      const extension = file.name.split(".").pop();
      const payload = {
        file: fileBase64Data,
        document_type: key,
        signatory: signatories[parseInt(key.split("_")[1])],
        metadata: {
          contentType: file.type,
          originalName: file.name,
          size: file.size,
          extension: extension,
        },
      };

      const response = await apiService.dealerOnboardingUploadDocuments(
        payload
      );

      if (response?.data?.error === false) {
        const previewUrl =
          response.data?.document?.url ?? URL.createObjectURL(file);
        const contentTypeFromResponse =
          response.data?.document?.contentType ||
          response.data?.document?.metadata?.contentType;
        const inferredType =
          contentTypeFromResponse ||
          file.type ||
          inferTypeFromUrlOrName(previewUrl) ||
          inferTypeFromUrlOrName(file.name);

        setSignatoryProofs((prev) => ({
          ...prev,
          [key]: { url: previewUrl, type: inferredType, name: file.name },
        }));
        setSignatoryUploadStatus((prev) => ({ ...prev, [key]: "success" }));
      } else {
        throw new Error(response?.data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setSignatoryUploadStatus((prev) => ({ ...prev, [key]: "error" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidating(true);
    try {
      const response = await apiService.validateDealerDocuments();

      await new Promise((r) => setTimeout(r, 500));
      setValidating(false);

      if (response.data.error && response.data.missing_fields) {
        const { business_docs, signatories } = response.data.missing_fields;

        const updatedStatus = {};
        business_docs.forEach((key) => (updatedStatus[key] = "error"));

        const updatedSignatoryStatus = {};
        signatories.forEach((s) => {
          s.missing_docs.forEach(
            (docKey) => (updatedSignatoryStatus[docKey] = "error")
          );
        });

        setUploadStatus((prev) => ({ ...prev, ...updatedStatus }));
        setSignatoryUploadStatus((prev) => ({
          ...prev,
          ...updatedSignatoryStatus,
        }));

        let sections = [];

        if (business_docs.length > 0) {
          const businessSection = `Business Documents:\n${business_docs
            .map((key) => `- ${key.replace(/_/g, " ")}`)
            .join("\n")}`;
          sections.push(businessSection);
        }

        const signatorySections = signatories
          .map((s) => {
            if (s.missing_docs && s.missing_docs.length > 0) {
              return `${s.name}:\n${s.missing_docs
                .map((docKey) => `- ${docKey.replace(/_/g, " ")}`)
                .join("\n")}`;
            }
            return null;
          })
          .filter(Boolean);

        if (signatorySections.length > 0) {
          sections.push(
            `Signatory Documents:\n${signatorySections.join("\n\n")}`
          );
        }

        const alertMessage =
          sections.length > 0
            ? `The following documents are missing or invalid:\n\n${sections.join(
                "\n\n"
              )}\n\nPlease upload the required documents and try again.`
            : "";

        alert(alertMessage);
      } else {
        alert("documents submitted successfully!");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error validating documents:", error);
      let alertMessage = "An error occurred while validating documents.";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        alertMessage += `\n\n${error.response.data.message}`;
      }
      alert(alertMessage);
    }
  };

  const navigateToPendingStage = async (validation_states) => {
    await new Promise((r) => setTimeout(r, 500));
    if (validation_states.details.status === "pending") {
      return navigate("/dealer-onboarding/dealer-details");
    } else if (validation_states.payment.status === "pending") {
      return navigate("/dealer-onboarding/subscription");
    } else if (
      validation_states.documents.status === "pending" ||
      validation_states.documents.status === "not_valid"
    ) {
      setCheckingNavigate(false);
      return;
    } else if (
      validation_states.documents.status === "validated" ||
      validation_states.contract.status === "pending"
    ) {
      return navigate("/dealer-onboarding/contract");
    }
  };

  // 🟩 fetch onboarding details
  useEffect(() => {
    const fetchDealerDetails = async () => {
      try {
        const response = await apiService.dealerOnboardingDetails({
          action: "get",
        });
        if (!response.data) return;
        if (response.data.error)
          return console.error("Error:", response.data.message);

        const validation_states = response.data.stage_validation_states;
        await navigateToPendingStage(validation_states);
        setIsNotValid(validation_states.documents.status === "not_valid");
        setNotValidComments(validation_states.documents.comments || "");

        setUserDetails(response.data.details);
        if (response.data?.documents) {
          // convert documents to { url, type, name }
          const documents = Object.keys(response.data.documents).reduce(
            (acc, key) => {
              const docEntry = response.data.documents[key];
              // docEntry might be { url } or { url, contentType, originalName } - handle both
              const url = docEntry?.url || docEntry;
              const contentType =
                docEntry?.contentType ||
                docEntry?.metadata?.contentType ||
                inferTypeFromUrlOrName(url);
              const originalName =
                docEntry?.originalName ||
                docEntry?.metadata?.originalName ||
                key;
              acc[key] = { url, type: contentType, name: originalName };
              return acc;
            },
            {}
          );
          setUploadedFiles(documents);
          setUploadStatus(
            Object.keys(documents).reduce((acc, key) => {
              acc[key] = "success";
              return acc;
            }, {})
          );

          if (response.data.documents.signatories) {
            const signatoriesList = response.data.documents.signatories.map(
              (signatory) => signatory.name
            );
            setSignatories(signatoriesList);
            const signatoryDocs = {};
            const signatoryStatuses = {};

            response.data.documents.signatories.forEach((signatory, index) => {
              Object.entries(signatory.docs).forEach(([docKey, docValue]) => {
                const url = docValue?.url || docValue;
                const contentType =
                  docValue?.contentType ||
                  docValue?.metadata?.contentType ||
                  inferTypeFromUrlOrName(url);
                const originalName =
                  docValue?.originalName ||
                  docValue?.metadata?.originalName ||
                  docKey;
                signatoryDocs[docKey] = {
                  url,
                  type: contentType,
                  name: originalName,
                };
                signatoryStatuses[docKey] = "success";
              });
            });

            setSignatoryProofs(signatoryDocs);
            setSignatoryUploadStatus(signatoryStatuses);
            setSignatories(signatoriesList);
          }
        }
      } catch (error) {
        console.error("Error fetching dealer details:", error);
      }
    };
    fetchDealerDetails();
  }, [navigate, currentUser, loading]);

  useEffect(() => {
    if (signatories.length > 0) {
      const lastIndex = signatories.length - 1;
      const ref = signatoryRefs.current[lastIndex];
      if (ref && typeof ref.scrollIntoView === "function") {
        ref.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [signatories.length]);

  if (checkingNavigate)
    return (
      <div className={`container ${styles.container}`}>
        <div className={styles.checkNavigate}>Please wait...</div>
      </div>
    );

  return (
    <div className={styles.wrapper}>
      {/* LEFT: INFO */}
      <div className={`container ${styles.container} ${styles.infoWrapper}`}>
        <h1 className={styles.title}>Upload Documents</h1>
        {isNotValid && (
          <div style={{ textAlign: "center", margin: "1em 0" }}>
            <button
              type="button"
              style={{
                background: "none",
                color: "#d32f2f",
                border: "none",
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "1em",
                padding: 0,
              }}
              onClick={() => setShowNotValidPopup(true)}
            >
              Please review the changes needed
            </button>
          </div>
        )}
        {showNotValidPopup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.4)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "2em 2.5em",
                borderRadius: "8px",
                maxWidth: "90vw",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
                textAlign: "center",
              }}
            >
              <h2 style={{ color: "#d32f2f", marginBottom: "1em" }}>
                Details Not Valid
              </h2>
              <div
                style={{
                  marginBottom: "1.5em",
                  color: "#333",
                  whiteSpace: "pre-wrap",
                  textAlign: "left",
                }}
              >
                {notValidComments}
              </div>
              <button
                style={{
                  padding: "0.5em 1.5em",
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "1em",
                }}
                onClick={() => setShowNotValidPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
        <h3 className={styles.companyName}>
          {userDetails?.business_name ?? userDetails?.name}
        </h3>

        {business_type_details_map.find(
          (t) => t.business_type === userDetails?.company_type
        )?.info_fields &&
          Object.entries(
            business_type_details_map.find(
              (t) => t.business_type === userDetails?.company_type
            ).info_fields
          ).map(([fieldKey, fieldLabel]) => (
            <div key={fieldKey} className={styles.info}>
              <h4>{fieldLabel}</h4>
              <p>{userDetails?.[fieldKey] || "N/A"}</p>
            </div>
          ))}

        <h2 className={styles.companyName}>Add a Signatory</h2>
        <div className={styles.signatoryWrapper}>
          {signatories.map((signatory, i) => (
            <div key={i} className={styles.info}>
              <h4>Name of the Signatory*</h4>
              <input
                type="text"
                className={styles.signatoryInput}
                ref={(el) => (signatoryRefs.current[i] = el)}
                onChange={(e) => {
                  const arr = [...signatories];
                  arr[i] = e.target.value;
                  setSignatories(arr);
                }}
                value={signatory}
                placeholder="Enter Name"
              />
            </div>
          ))}
        </div>
        <button
          className={styles.addSignatoryBtn}
          onClick={() => setSignatories([...signatories, ""])}
        >
          Add Signatory
        </button>
      </div>

      {/* RIGHT: UPLOADS */}
      <div className={`container ${styles.container} ${styles.uploadsWrapper}`}>
        <div className={styles.uploadGroup}>
          <h2>Upload Business Proofs</h2>
          <div className={styles.uploadArea}>
            <div className={styles.titleGroup}>
              <h4>Company Details</h4>
              <button onClick={() => setMinimized((m) => !m)}>
                <img
                  src={minimized ? eyeIcon : eyeIconStriked}
                  alt="Show/hide button"
                />{" "}
                {minimized ? "Show" : "Hide"}
              </button>
            </div>

            <div
              className={`${styles.uploadsList} ${
                minimized ? styles.minimized : ""
              }`}
            >
              {documents_required_map[userDetails?.company_type]?.map((doc) => {
                const status = uploadStatus[doc.key];
                const uploaded = status === "success";
                const fileObj = uploadedFiles[doc.key];
                return (
                  <div
                    key={doc.key}
                    className={`${styles.uploadItem} ${
                      uploaded ? styles.uploaded : ""
                    } ${status === "error" ? styles.error : ""}`}
                  >
                    {renderPreview(fileObj, doc.name, () => {
                      document.getElementById(`file-input-${doc.key}`).click();
                    })}
                    <label className={styles.uploadBox}>
                      {status === "uploading" ? (
                        <span>Uploading...</span>
                      ) : (
                        <>{doc.name}*</>
                      )}
                      <input
                        id={`file-input-${doc.key}`}
                        type="file"
                        accept=".pdf,.jpg,.png"
                        style={{ display: "none" }}
                        onChange={(e) => handleUpload(e, doc.key)}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {signatories.map((signatory, i) => (
          <div className={styles.uploadGroup} key={`signatory-group-${i}`}>
            <h2>Add Signatory Proofs</h2>
            <div className={styles.uploadArea}>
              <div className={styles.titleGroup}>
                <h4>
                  {signatory?.trim() !== ""
                    ? `${signatory}'s Proofs`
                    : `Signatory ${i + 1} Proofs`}
                </h4>
                <button onClick={() => setMinimized((m) => !m)}>
                  <img
                    src={minimized ? eyeIcon : eyeIconStriked}
                    alt="Show/hide button"
                  />{" "}
                  {minimized ? "Show" : "Hide"}
                </button>
              </div>

              <div
                className={`${styles.uploadsList} ${
                  minimized ? styles.minimized : ""
                }`}
              >
                {documents_required_map["signatory_proofs"].map((doc) => {
                  const key = `signatory_${i}_${doc.key}`;
                  const status = signatoryUploadStatus[key];
                  const uploaded = status === "success";
                  const fileObj = signatoryProofs[key];
                  return (
                    <div
                      key={key}
                      className={`${styles.uploadItem} ${
                        uploaded ? styles.uploaded : ""
                      } ${status === "error" ? styles.error : ""}`}
                    >
                      {renderPreview(fileObj, doc.name, () => {
                        document.getElementById(`file-input-${key}`).click();
                      })}
                      <label className={styles.uploadBox}>
                        {status === "uploading" ? (
                          <span>Uploading...</span>
                        ) : (
                          <>{doc.name}*</>
                        )}
                        <input
                          id={`file-input-${key}`}
                          type="file"
                          accept=".pdf,.jpg,.png"
                          style={{ display: "none" }}
                          onChange={(e) => handleSignatoryUpload(e, key)}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        <button
          className={styles.submitBtn}
          disabled={validating}
          onClick={handleSubmit}
        >
          {isNotValid ? "Resubmit for Validation" : "Submit Documents"}
        </button>
      </div>
    </div>
  );
}
